/**
 * Auth declaw — fleet flag + returning-user gate for involuntary logout suppression.
 * Flag fetch is public (no auth headers). Defaults to disabled on any failure.
 */

import { CLOUD_API_URL } from '../config/serverConfig';
import { isValidRefreshToken } from './authTokens';

const SS_CONNECTOR_AUTH_JSON = 'maestro_connector_has_auth_json';
const LS_AUTHED_ONCE = 'maestro_authed_once_v1';
const SS_EXPLICIT_SIGNOUT = 'maestro_explicit_signout';
const SS_SESSION_STARTED = 'maestro_session_started_at';

let _flagLoaded = false;
let _flagEnabled = false;
let _flagInitPromise: Promise<void> | null = null;

export function isAuthDeclawEnabled(): boolean {
  return _flagEnabled;
}

export function hasStoredSession(): boolean {
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { accessToken?: string; refreshToken?: string };
    return !!(parsed?.accessToken && isValidRefreshToken(parsed?.refreshToken));
  } catch {
    return false;
  }
}

export function hasAuthedAtLeastOnceThisInstall(): boolean {
  try {
    return localStorage.getItem(LS_AUTHED_ONCE) === '1';
  } catch {
    return false;
  }
}

export function noteAuthedOnceThisInstall(): void {
  try {
    localStorage.setItem(LS_AUTHED_ONCE, '1');
  } catch { /* ignore */ }
  noteSessionStartedNow();
}

export function noteSessionStartedNow(): void {
  try {
    if (!sessionStorage.getItem(SS_SESSION_STARTED)) {
      sessionStorage.setItem(SS_SESSION_STARTED, String(Date.now()));
    }
  } catch { /* ignore */ }
}

export function getElapsedSessionMs(): number | null {
  try {
    const raw = sessionStorage.getItem(SS_SESSION_STARTED);
    if (!raw) return null;
    const started = Number(raw);
    if (!Number.isFinite(started) || started <= 0) return null;
    return Math.max(0, Date.now() - started);
  } catch {
    return null;
  }
}

export function markExplicitSignOut(): void {
  try {
    sessionStorage.setItem(SS_EXPLICIT_SIGNOUT, '1');
    sessionStorage.removeItem(SS_CONNECTOR_AUTH_JSON);
  } catch { /* ignore */ }
}

export function clearExplicitSignOut(): void {
  try {
    sessionStorage.removeItem(SS_EXPLICIT_SIGNOUT);
  } catch { /* ignore */ }
}

function hasExplicitSignOut(): boolean {
  try {
    return sessionStorage.getItem(SS_EXPLICIT_SIGNOUT) === '1';
  } catch {
    return false;
  }
}

export function hasConnectorStoredSession(): boolean {
  try {
    return sessionStorage.getItem(SS_CONNECTOR_AUTH_JSON) === '1';
  } catch {
    return false;
  }
}

export function noteConnectorStoredSession(present: boolean): void {
  try {
    if (present) {
      sessionStorage.setItem(SS_CONNECTOR_AUTH_JSON, '1');
    } else {
      sessionStorage.removeItem(SS_CONNECTOR_AUTH_JSON);
    }
  } catch { /* ignore */ }
}

/** Returning user = authed once + not explicit sign-out + (localStorage OR connector auth.json). */
export function isReturningUser(): boolean {
  if (!hasAuthedAtLeastOnceThisInstall() || hasExplicitSignOut()) {
    return false;
  }
  return hasStoredSession() || hasConnectorStoredSession();
}

export function shouldDeclawInvoluntaryLogout(): boolean {
  return isAuthDeclawEnabled() && isReturningUser();
}

/** Belt-and-suspenders AppLayout gate — keep shell when declaw would suppress logout. */
export function shouldKeepPlaybackShellDespiteAuthLoss(): boolean {
  return shouldDeclawInvoluntaryLogout()
    && (hasStoredSession() || hasConnectorStoredSession());
}

export async function initAuthDeclawFlag(): Promise<void> {
  if (_flagInitPromise) return _flagInitPromise;
  _flagInitPromise = (async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      let res: Response;
      try {
        res = await fetch(`${CLOUD_API_URL}/api/config/auth-declaw`, {
          method: 'GET',
          credentials: 'omit',
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!res.ok) {
        _flagEnabled = false;
        return;
      }
      const data = await res.json().catch(() => null) as { enabled?: unknown } | null;
      _flagEnabled = data?.enabled === true;
    } catch {
      _flagEnabled = false;
    } finally {
      _flagLoaded = true;
    }
  })();
  return _flagInitPromise;
}

export function isAuthDeclawFlagLoaded(): boolean {
  return _flagLoaded;
}

export function resetAuthDeclawForTests(): void {
  _flagLoaded = false;
  _flagEnabled = false;
  _flagInitPromise = null;
}

export function setAuthDeclawEnabledForTests(enabled: boolean): void {
  _flagLoaded = true;
  _flagEnabled = enabled;
}
